import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Page from "components/page/Page";
import { useAppSelector } from "hooks/redux";
import { StatusCode } from "api/protocols";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import { Select } from "components/select/index";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import { isEqualWith } from "lodash";
import {
  DeleteDepartments,
  GetDepartmentDefaults,
  GetDepartmentDeleteEligibility,
  GetDepartments,
  IDepartmentDefaults,
  PutDepartmentDefaults,
  PutDepartments,
  UpdateAllDepartmentProducts,
} from "api/rpc/2024-04/facilityAdmin/facility/department";
import {
  Department,
  ICategories,
  IDepartments,
  TProductType,
  TProductVendor,
  TProductUnit,
} from "redux/reducers/models/product";
import { TAccountingReference, TTaxLines } from "redux/reducers/models/facility";
import { formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";
import { showError, showSuccess } from "redux/actions/ui";
import { useDispatch } from "react-redux";
import axios, { CancelToken } from "axios";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { GetProductTypes } from "api/rpc/2024-04/facilityAdmin/product/type";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { TKitchenLocation } from "redux/reducers/models/kitchen";
import { GetKitchenLocations } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/location";
import { GetTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import { GetProductUnits } from "api/rpc/2024-04/facilityAdmin/product/unit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import ProductLogisticsTable from "./components/ProductLogisticsTable";
import FinancialTable from "./components/FinancialTable";
import SalesChannelTable from "./components/SalesChannelTable";
import "./departments.scss";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";

type EditType = "department" | "category" | "subcategory";

interface IDeleteDepartmentParams {
  id: number;
  title: string;
  type: EditType;
  parent_id: number | null;
  migrate_id?: number;
}

interface IEditDepartmentState {
  id: number | null;
  title: string;
  type: EditType | null;
  parent_id: number | null;
  updateProductsStatus: "only_new" | "all_new_and_existing";
  updateAllProductsOpen?: boolean;
  productType?: string;
  vendor?: TProductVendor;
  vendor_id?: number;
  accounting_reference?: TAccountingReference;
  accounting_reference_id?: number;
  kitchen_location?: TKitchenLocation;
  kitchen_location_id?: number;
  kitchen_location_2_id?: number;
  kitchen_location_3_id?: number;
  customer_required?: boolean;
  fulfillment_required?: boolean;
  shipping_required?: boolean;
  track_inventory?: boolean;
  cannot_be_sold?: boolean;
  online_store?: boolean;
  tax_exempt?: boolean;
  tax_line_ids?: number[];
  product_unit_id?: number;
}

const LOGISTICS_KEYS = [
  "productTypeSearch",
  "accountRefSearch",
  "vendorSearch",
  "kitchenLocationSearch",
  "kitchenLocation2Search",
  "kitchenLocation3Search",
  "productUnitSearch",
] as const;

type LogisticsKey = typeof LOGISTICS_KEYS[number];

export default function EditDepartment() {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const { type, id } = useParams<{ type: string; id: string }>();
  const dispatch = useDispatch();
  const { authStore, facilityStore } = useAppSelector(store => store);
  const userCanEditProducts = authStore.user?.permissions?.products_edit;
  const canEditDepartmentDefaults =
    !!authStore?.user?.permissions?.departments_edit && !!facilityStore?.facility?.permissions?.departments_edit;
  const defaultsTogglesDisabled = !canEditDepartmentDefaults;

  const departmentType: EditType =
    type === "department" || type === "category" || type === "subcategory" ? type : "department";

  const [editState, setEditState] = useState<IEditDepartmentState>({
    id: null,
    title: "",
    type: departmentType,
    parent_id: null,
    updateProductsStatus: "only_new",
    updateAllProductsOpen: false,
    productType: undefined,
    vendor: undefined,
    vendor_id: undefined,
    accounting_reference: undefined,
    accounting_reference_id: undefined,
    kitchen_location: undefined,
    kitchen_location_id: undefined,
    kitchen_location_2_id: undefined,
    kitchen_location_3_id: undefined,
    customer_required: false,
    fulfillment_required: false,
    shipping_required: false,
    track_inventory: false,
    cannot_be_sold: false,
    online_store: false,
    tax_exempt: false,
    tax_line_ids: [],
    product_unit_id: undefined,
  });

  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [entityTitle, setEntityTitle] = useState("");
  const [parentTitle, setParentTitle] = useState("");
  const [isUsingParentDefaults, setIsUsingParentDefaults] = useState(false);
  const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);
  const [deleteChildrenWarningOpen, setDeleteChildrenWarningOpen] = useState(false);
  const [deleteChildrenWarningMessage, setDeleteChildrenWarningMessage] = useState("");
  const [deleteMigrationOpen, setDeleteMigrationOpen] = useState(false);
  const [deleteMigrationTargetId, setDeleteMigrationTargetId] = useState<number | null>(null);
  const [stateBeforeChanges, setStateBeforeChanges] = useState<IEditDepartmentState | undefined>(undefined);

  const [productTypes, setProductTypes] = useState<TProductType[]>([]);
  const [accountingReferences, setAccountingReferences] = useState<TAccountingReference[]>([]);
  const [vendors, setVendors] = useState<TProductVendor[]>([]);
  const [kitchenLocations, setKitchenLocations] = useState<TKitchenLocation[]>([]);
  const [taxLines, setTaxLines] = useState<TTaxLines[]>([]);
  const [productUnits, setProductUnits] = useState<TProductUnit[]>([]);

  const taxLinesForFacilityProvince = useMemo(() => {
    if (facilityStore.facility?.province_id === null) {
      return taxLines;
    }
    return taxLines.filter(taxLine => taxLine.province_id === facilityStore.facility?.province_id);
  }, [taxLines, facilityStore.facility?.province_id]);

  const [applyDefaultsPopupOpen, setApplyDefaultsPopupOpen] = useState(false);
  const hasDefaultsChanged = stateBeforeChanges ? !isEqualWith(stateBeforeChanges, editState) : false;

  const [logisticsCard, setLogisticsCard] = useState<Record<LogisticsKey, string>>({
    productTypeSearch: "",
    accountRefSearch: "",
    vendorSearch: "",
    kitchenLocationSearch: "",
    kitchenLocation2Search: "",
    kitchenLocation3Search: "",
    productUnitSearch: "",
  });

  function mapDefaultsFromResponse(res: any): Partial<IEditDepartmentState> | null {
    if (!res || res.status !== StatusCode.OK || !res.data) {
      return null;
    }
    const data = res.data;
    return {
      productType: data.type,
      vendor_id: data.vendor_id,
      accounting_reference_id: data.accounting_reference_id,
      kitchen_location_id: data.kitchen_location_id,
      kitchen_location_2_id: data.kitchen_location_2_id,
      kitchen_location_3_id: data.kitchen_location_3_id,
      customer_required: data.customer_required,
      fulfillment_required: data.fulfillment_required,
      shipping_required: data.shipping_required,
      track_inventory: data.track_inventory,
      cannot_be_sold: data.cannot_be_sold,
      online_store: data.online_store,
      tax_exempt: data.tax_exempt,
      tax_line_ids: data.tax_line_ids,
      product_unit_id: data.product_unit_id,
    };
  }

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadDepartment();
    void loadProductTypes(source.token);
    void loadVendors(source.token);
    void loadAccountingReferences(source.token);
    void loadKitchenLocations(source.token);
    void loadTaxLines(source.token);
    void loadProductUnits(source.token);
    return () => source.cancel();
  }, [departmentType, id]);

  useEffect(() => {
    if (editState?.productType) {
      setLogisticsCard(prev => ({ ...prev, productTypeSearch: editState.productType ?? "" }));
    }
  }, [editState?.productType]);

  useEffect(() => {
    const accId = editState?.accounting_reference_id;
    if (accId === null || accId === undefined) {
      setLogisticsCard(prev => ({ ...prev, accountRefSearch: "" }));
      return;
    }
    if (!accountingReferences?.length) {
      return;
    }
    const ref = accountingReferences.find(
      (r: TAccountingReference) => String(r?.id) === String(accId),
    );
    if (!ref) {
      return;
    }
    setLogisticsCard(prev => ({
      ...prev,
      accountRefSearch: formatAccountingReferenceLabel(ref.title ?? "", ref.reference_number),
    }));
  }, [editState?.accounting_reference_id, accountingReferences]);

  useEffect(() => {
    if (vendors && editState?.vendor_id) {
      const vendor = vendors?.find(vendorItem => vendorItem.id === editState.vendor_id);
      setLogisticsCard(prev => ({ ...prev, vendorSearch: vendor?.title ?? prev.vendorSearch }));
    }
  }, [vendors, editState?.vendor_id]);

  useEffect(() => {
    if (kitchenLocations?.length && editState?.kitchen_location_id != null) {
      const loc = kitchenLocations?.find(kitchenLocation => kitchenLocation.id === editState.kitchen_location_id);
      setLogisticsCard(prev => ({ ...prev, kitchenLocationSearch: loc?.title ?? "" }));
    }
  }, [kitchenLocations, editState?.kitchen_location_id]);

  useEffect(() => {
    if (kitchenLocations?.length && editState?.kitchen_location_2_id != null) {
      const loc = kitchenLocations?.find(kitchenLocation => kitchenLocation.id === editState.kitchen_location_2_id);
      setLogisticsCard(prev => ({ ...prev, kitchenLocation2Search: loc?.title ?? "" }));
    }
  }, [kitchenLocations, editState?.kitchen_location_2_id]);

  useEffect(() => {
    if (kitchenLocations?.length && editState?.kitchen_location_3_id != null) {
      const loc = kitchenLocations?.find(kitchenLocation => kitchenLocation.id === editState.kitchen_location_3_id);
      setLogisticsCard(prev => ({ ...prev, kitchenLocation3Search: loc?.title ?? "" }));
    }
  }, [kitchenLocations, editState?.kitchen_location_3_id]);

  useEffect(() => {
    if (productUnits?.length && editState?.product_unit_id != null) {
      const unit = productUnits?.find(productUnit => productUnit.id === editState.product_unit_id);
      setLogisticsCard(prev => ({ ...prev, productUnitSearch: unit?.name ?? "" }));
    }
  }, [productUnits, editState?.product_unit_id]);

  async function loadDepartment() {
    const res = await GetDepartments({ extended: true }, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    const current = res.data?.find((dept: any) => dept.id === Number(id));
    if (!current || current.type !== departmentType) {
      history.push("/admin/settings/departments");
      return;
    }

    let defaults: Partial<IEditDepartmentState> | null = null;

    if (departmentType === "department") {
      const defaultsRes = await GetDepartmentDefaults({ department_id: current.id }, true);
      defaults = mapDefaultsFromResponse(defaultsRes);
      setIsUsingParentDefaults(false);
    } else if (departmentType === "category") {
      let defaultsRes = await GetDepartmentDefaults({ department_id: current.id }, true);
      defaults = mapDefaultsFromResponse(defaultsRes);
      if (!defaults) {
        setIsUsingParentDefaults(true);
        defaultsRes = await GetDepartmentDefaults({ department_id: current.parent_id }, true);
        defaults = mapDefaultsFromResponse(defaultsRes);
      } else {
        setIsUsingParentDefaults(false);
      }
      const parentDept = res.data?.find((department: any) => department.id === current.parent_id);
      setParentTitle(parentDept?.title ?? "");
    } else {
      const ownDefaultsRes = await GetDepartmentDefaults({ department_id: current.id }, true);
      defaults = mapDefaultsFromResponse(ownDefaultsRes);
      if (!defaults) {
        setIsUsingParentDefaults(true);
        const defaultsRes = await GetDepartmentDefaults({ department_id: current.parent_id }, true);
        defaults = mapDefaultsFromResponse(defaultsRes);
        if (!defaults) {
          const parentCategory = res.data?.find((department: any) => department.id === current.parent_id);
          if (parentCategory) {
            const deptRes = await GetDepartmentDefaults({ department_id: parentCategory.parent_id }, true);
            defaults = mapDefaultsFromResponse(deptRes);
          }
        }
      } else {
        setIsUsingParentDefaults(false);
      }
      const parentCat = res.data?.find((department: any) => department.id === current.parent_id);
      setParentTitle(parentCat?.title ?? "");
    }

    setAllDepartments(res.data ?? []);
    setEntityTitle(current.title);

    const initialState: IEditDepartmentState = {
      id: current.id,
      title: current.title,
      type: departmentType,
      parent_id: current.parent_id ?? null,
      updateProductsStatus: "only_new",
      updateAllProductsOpen: false,
      productType: undefined,
      vendor: undefined,
      vendor_id: undefined,
      accounting_reference: undefined,
      accounting_reference_id: undefined,
      kitchen_location: undefined,
      kitchen_location_id: undefined,
      kitchen_location_2_id: undefined,
      kitchen_location_3_id: undefined,
      customer_required: false,
      fulfillment_required: false,
      shipping_required: false,
      track_inventory: false,
      cannot_be_sold: false,
      online_store: false,
      tax_exempt: false,
      tax_line_ids: [],
      product_unit_id: undefined,
      ...(defaults ?? {}),
    };

    setEditState(initialState);
    setStateBeforeChanges(initialState);
  }

  async function loadVendors(token: CancelToken) {
    const res = await GetVendor(null, false, token);
    if (token?.reason) {
      return;
    }
    if (res.status === StatusCode.OK) {
      setVendors(res.data ?? []);
    }
  }

  async function loadProductTypes(token: CancelToken) {
    const res = await GetProductTypes(null, false, token);
    if (token?.reason) {
      return;
    }
    if (res.status === StatusCode.OK) {
      setProductTypes(res.data ?? []);
    }
  }

  async function loadAccountingReferences(token?: CancelToken) {
    const res = await GetAccountingReference(null, token ? false : true, token);
    if (token?.reason) {
      return;
    }
    if (res.status === StatusCode.OK) {
      setAccountingReferences(res.data ?? []);
    }
  }

  async function loadKitchenLocations(token?: CancelToken) {
    const res = await GetKitchenLocations(null, token ? false : true, token);
    if (token?.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading kitchen locations.")));
    }
    setKitchenLocations(res.status === StatusCode.OK ? res.data ?? [] : []);
  }

  async function loadTaxLines(token?: CancelToken) {
    const res = await GetTaxLine(null, token ? false : true, token);
    if (token?.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tax lines.")));
    }
    setTaxLines(res.status === StatusCode.OK ? res.data ?? [] : []);
  }

  async function loadProductUnits(token?: CancelToken) {
    const res = await GetProductUnits(null, token ? false : true, token);
    if (token?.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading product units.")));
    }
    setProductUnits(res.status === StatusCode.OK ? res.data ?? [] : []);
  }

  async function saveDepartment() {
    if (editState.id == null) {
      return;
    }
    if (!hasDefaultsChanged) {
      return;
    }

    const before = stateBeforeChanges;
    const changed = <StateKey extends keyof IEditDepartmentState>(key: StateKey) => editState[key] !== before?.[key];

    const taxBefore = JSON.stringify([...(before?.tax_line_ids ?? [])].sort());
    const taxAfter = JSON.stringify([...(editState.tax_line_ids ?? [])].sort());

    const defaultsParams: IDepartmentDefaults = { id: editState.id };

    if (changed("productType") && editState.productType != null) {
      defaultsParams.type = editState.productType;
    }
    if (changed("vendor_id") && editState.vendor_id != null) {
      defaultsParams.vendor_id = editState.vendor_id;
    }
    if (changed("accounting_reference_id") && editState.accounting_reference_id != null) {
      defaultsParams.accounting_reference_id = editState.accounting_reference_id;
    }
    if (
      changed("kitchen_location_id") &&
      editState.kitchen_location_id != null &&
      editState.kitchen_location_id !== -1
    ) {
      defaultsParams.kitchen_location_id = editState.kitchen_location_id;
    }
    if (
      changed("kitchen_location_2_id") &&
      editState.kitchen_location_2_id != null &&
      editState.kitchen_location_2_id !== -1
    ) {
      defaultsParams.kitchen_location_id_2 = editState.kitchen_location_2_id;
    }
    if (
      changed("kitchen_location_3_id") &&
      editState.kitchen_location_3_id != null &&
      editState.kitchen_location_3_id !== -1
    ) {
      defaultsParams.kitchen_location_id_3 = editState.kitchen_location_3_id;
    }
    if (changed("product_unit_id") && editState.product_unit_id != null) {
      defaultsParams.product_unit_id = editState.product_unit_id;
    }

    const boolFields = [
      "cannot_be_sold",
      "fulfillment_required",
      "customer_required",
      "shipping_required",
      "track_inventory",
      "online_store",
      "tax_exempt",
    ] as const;
    for (const field of boolFields) {
      if (changed(field)) {
        defaultsParams[field] = editState[field] ?? false;
      }
    }

    if (taxAfter !== taxBefore) {
      defaultsParams.tax_line_ids = editState.tax_line_ids;
    }

    if (Object.keys(defaultsParams).filter(paramKey => paramKey !== "id").length > 0) {
      const putRes = await PutDepartmentDefaults(defaultsParams, true);

      if (putRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating department defaults")));
        return;
      }

      if (editState.updateProductsStatus === "all_new_and_existing") {
        const updateRes = await UpdateAllDepartmentProducts(defaultsParams, true);
        if (updateRes.status !== StatusCode.OK) {
          const msg =
            departmentType === "department"
              ? t("Error updating all products in department")
              : departmentType === "category"
              ? t("Error updating all products in category")
              : t("Error updating all products in subcategory");
          dispatch(showError(msg));
          return;
        }
      }
    }

    if (
      editState.title !== String(stateBeforeChanges?.title) ||
      editState.parent_id !== stateBeforeChanges?.parent_id
    ) {
      const putBody: IDepartments = {
        id: editState.id,
        title: editState.title,
        type: editState.type ?? "department",
        ...(editState.parent_id != null && { parent_id: editState.parent_id }),
      };

      const res = await PutDepartments(putBody, true);
      if (res.status !== StatusCode.OK) {
        const msg =
          departmentType === "department"
            ? t("Error saving department")
            : departmentType === "category"
            ? t("Error saving category")
            : t("Error saving subcategory");
        dispatch(showError(msg));
        return;
      }
    }

    dispatch(showSuccess(t("Changes saved successfully")));
    setStateBeforeChanges(editState);
    setApplyDefaultsPopupOpen(false);
  }

  async function deleteDepartment() {
    if (editState.id == null) {
      return;
    }
    const deleteParams: IDeleteDepartmentParams = {
      id: editState.id,
      title: editState.title,
      type: editState.type ?? "department",
      ...(editState.parent_id != null && { parent_id: editState.parent_id }),
      ...(deleteMigrationTargetId != null ? { migrate_id: deleteMigrationTargetId } : {}),
    };

    const res = await DeleteDepartments(deleteParams, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing")));
      return;
    }

    history.push("/admin/settings/departments");
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setEditState(prev => ({ ...prev, [id]: value }));
  }

  function handleParentChange(value: number) {
    setEditState(prev => ({ ...prev, parent_id: value }));
  }

  function logisticsCardSearch(key: LogisticsKey, query: string) {
    setLogisticsCard(prev => ({ ...prev, [key]: query }));
    if (logisticsCard[key] != null && query?.length === 0) {
      const clearMap: Partial<Record<LogisticsKey, keyof IEditDepartmentState>> = {
        accountRefSearch: "accounting_reference_id",
        productTypeSearch: "productType",
        vendorSearch: "vendor_id",
        kitchenLocationSearch: "kitchen_location_id",
        kitchenLocation2Search: "kitchen_location_2_id",
        kitchenLocation3Search: "kitchen_location_3_id",
        productUnitSearch: "product_unit_id",
      };
      const stateKey = clearMap[key];
      if (stateKey) {
        setEditState(prev => ({ ...prev, [stateKey]: undefined }));
      }
    }
  }

  function getTaxNames() {
    const names: string[] = [];
    for (const taxLineId of editState.tax_line_ids ?? []) {
      names.push(taxLines?.find(taxLine => taxLine.id === taxLineId)?.name ?? "");
    }
    return names.join(", ");
  }

  const toggleTaxLines = (value: number) => {
    setEditState(prev => {
      const ids = prev.tax_line_ids ?? [];
      return {
        ...prev,
        tax_line_ids: ids.includes(value) ? ids.filter(existingId => existingId !== value) : [...ids, value],
      };
    });
  };

  const departments = (allDepartments ?? []).filter((dept): dept is IDepartments => dept.type === "department");
  const categories = (allDepartments ?? []).filter((dept): dept is ICategories => dept.type === "category");

  function renderParentOptions() {
    if (departmentType === "category") {
      return departments.map((dept: any) => (
        <Option key={dept.id} value={dept.id} name={dept.title}>
          {dept.title}
        </Option>
      ));
    }
    if (departmentType === "subcategory") {
      const result: React.ReactNode[] = [];
      for (const dept of departments ?? []) {
        const deptCategories = (categories ?? []).filter(category => category.parent_id === dept.id);
        if (deptCategories.length === 0) {
          continue;
        }
        result.push(
          <Option key={`dept-header-${dept.id}`} value={`dept-header-${dept.id}`} disabled name={dept.title}>
            {dept.title}
          </Option>,
        );
        for (const category of deptCategories) {
          result.push(
            <Option key={category.id} value={category.id} name={category.title}>
              {category.title}
            </Option>,
          );
        }
      }
      return result;
    }
    return null;
  }

  const basePath = "/admin/settings/departments";
  const showParentSelect = departmentType !== "department";

  const pageTitle =
    departmentType === "department"
      ? t("Edit Department: ") + entityTitle
      : departmentType === "category"
      ? t("Edit category: ") + entityTitle
      : t("Edit subcategory: ") + entityTitle;

  const detailsCardTitle =
    departmentType === "department"
      ? t("Department details")
      : departmentType === "category"
      ? t("Category details")
      : t("Subcategory details");

  const nameLabel =
    departmentType === "department"
      ? t("Title")
      : departmentType === "category"
      ? t("Category name")
      : t("Subcategory name");

  const namePlaceholder =
    departmentType === "department"
      ? t("Title")
      : departmentType === "category"
      ? t("Enter category name")
      : t("Enter subcategory name");

  const parentSelectLabel = departmentType === "category" ? t("Parent department") : t("Parent category");

  const parentSelectPlaceholder =
    departmentType === "category" ? t("Select parent department") : t("Select parent category");

  function getCurrentDepartmentFromAllDepartments(): Department | null {
    const currentId = editState.id;
    if (currentId == null) {
      return null;
    }
    return (allDepartments ?? []).find(dept => dept.id === currentId) ?? null;
  }

  type MigrationTarget = { id: number; title: string; type: EditType; depth: 0 | 1 | 2 };

  function getMigrationTargets(department: Department | null): MigrationTarget[] {
    if (!department) {
      return [];
    }

    const available = allDepartments.filter(item => item.id !== department.id);
    const byTitle = (first: Department, second: Department) =>
      first.title.localeCompare(second.title, undefined, { sensitivity: "base" });

    const targets: MigrationTarget[] = [];

    for (const dept of available.filter(item => item.type === "department").sort(byTitle)) {
      targets.push({ id: dept.id, title: dept.title, type: dept.type, depth: 0 });

      for (const category of available
        .filter(item => item.type === "category" && item.parent_id === dept.id)
        .sort(byTitle)) {
        targets.push({ id: category.id, title: category.title, type: category.type, depth: 1 });

        for (const subcategory of available
          .filter(item => item.type === "subcategory" && item.parent_id === category.id)
          .sort(byTitle)) {
          targets.push({ id: subcategory.id, title: subcategory.title, type: subcategory.type, depth: 2 });
        }
      }
    }

    return targets;
  }

  async function onDeleteClicked() {
    const department = getCurrentDepartmentFromAllDepartments();
    if (!department || editState.id == null || editState.type == null) {
      return;
    }

    const res = await GetDepartmentDeleteEligibility({ id: editState.id }, true);
    if (res.status !== StatusCode.OK || res.data == null) {
      dispatch(showError(t("Error removing")));
      return;
    }

    const check = res.data;

    if (check.has_category_children) {
      setDeleteChildrenWarningMessage(t("You must delete all categories within this department before deleting it"));
      setDeleteChildrenWarningOpen(true);
      return;
    }
    if (check.has_subcategory_children) {
      setDeleteChildrenWarningMessage(t("You must delete all subcategories within this category before deleting it"));
      setDeleteChildrenWarningOpen(true);
      return;
    }

    setDeleteMigrationTargetId(null);

    if (check.has_products) {
      setDeleteMigrationOpen(true);
    } else {
      setDeleteModalVisibility(true);
    }
  }

  const handleConfirmDelete = (): void => {
    void deleteDepartment();
  };

  const handleCloseChildrenWarning = (): void => {
    setDeleteChildrenWarningOpen(false);
  };

  const handleConfirmMigrateAndDelete = (): void => {
    void deleteDepartment();
  };

  const deleteDescription =
    departmentType === "department"
      ? t("Are you sure you want to delete this department?")
      : departmentType === "category"
      ? t("Are you sure you want to delete this category?")
      : t("Are you sure you want to delete this subcategory?");

  const configureSubtitle =
    departmentType === "department"
      ? t("Define which fields are visible and set default values for product created in this department.")
      : departmentType === "category"
      ? t("Define which fields are visible and set default values for product created in this category.")
      : t("Define which fields are visible and set default values for product created in this subcategory.");

  return (
    <div>
      <Page
        title={pageTitle}
        notificationBarProps={
          hasDefaultsChanged
            ? {
                isVisible: true,
                onCancel: () => {
                  if (stateBeforeChanges) {
                    setEditState(stateBeforeChanges);
                  }
                },
                onAction: () => {
                  if (hasDefaultsChanged) {
                    setApplyDefaultsPopupOpen(true);
                  }
                },
              }
            : undefined
        }
        primaryAction={{
          content: t("Save changes"),
          action: () => {
            if (hasDefaultsChanged) {
              setApplyDefaultsPopupOpen(true);
            } else {
              void saveDepartment();
            }
          },
        }}
        secondaryActions={[{ content: t("Delete"), action: () => void onDeleteClicked() }]}
        breadcrumbs={[
          { label: t("Settings"), url: "/admin/settings" },
          { label: t("Departments & categories"), url: basePath },
          { label: entityTitle, url: `${basePath}/${departmentType}/${id}` },
        ]}
      >
        <Card title={detailsCardTitle}>
          <Card.Section>
            <div style={{ marginTop: "-24px", marginBottom: "24px" }}>
              <hr />
            </div>
            <Input
              value={editState.title}
              label={nameLabel}
              id="title"
              onChange={handleInputChange}
              placeholder={namePlaceholder}
            />
            {showParentSelect && (
              <div className="mt-4">
                <Select
                  key={`parent-${editState.id ?? "loading"}-${editState.parent_id}`}
                  label={parentSelectLabel}
                  onChange={handleParentChange}
                  defaultValue={editState.parent_id}
                  placeholder={parentSelectPlaceholder}
                  helpText={
                    departmentType === "subcategory"
                      ? t("Select a category that this subcategory belongs to.")
                      : departmentType === "category"
                      ? t("Assign this category to a department (e.g. Golf, F&B).")
                      : undefined
                  }
                >
                  {renderParentOptions()}
                </Select>
              </div>
            )}
          </Card.Section>
        </Card>

        {userCanEditProducts && (
          <Card title={t("Configure product values")} subtitle={configureSubtitle}>
            <div style={{ margin: "24px" }}>
              <hr />
            </div>
            {departmentType !== "department" && isUsingParentDefaults && (
              <div className="default-banner-container">
                <p className="default-banner-container_title">
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: "8px", fontSize: "16px" }} />
                  {departmentType === "category"
                    ? t("Using default settings from department: ") + parentTitle
                    : t("Using default settings from category: ") + parentTitle}
                </p>
                <p className="default-banner-container_text">
                  {departmentType === "category"
                    ? t("You can override specific fields below if this category requires unique product values.")
                    : t("You can override specific fields below if this variation requires unique product values.")}
                </p>
              </div>
            )}
            <div style={{ margin: "24px" }}>
              <Card
                title="Product Logistics"
                subtitle="Set default product classification, vendor, and fulfillment requirements."
              >
                <div style={{ marginTop: "24px" }}>
                  <ProductLogisticsTable
                    state={editState}
                    setState={setEditState}
                    logistics={logisticsCard}
                    onSearch={logisticsCardSearch}
                    productTypes={productTypes}
                    vendors={vendors}
                    kitchenLocations={kitchenLocations}
                    togglesDisabled={defaultsTogglesDisabled}
                    productUnits={productUnits}
                  />
                </div>
              </Card>
            </div>
            <div style={{ margin: "24px" }}>
              <Card title="Financial & taxes" subtitle="Configure default GL codes and tax rules for these products.">
                <div style={{ marginTop: "24px" }}>
                  <FinancialTable
                    state={editState}
                    setState={setEditState}
                    search={logisticsCard}
                    onSearch={logisticsCardSearch}
                    accountingReferences={accountingReferences}
                    taxLines={taxLinesForFacilityProvince}
                    taxNamesPlaceholder={getTaxNames()}
                    onTaxLinesToggle={toggleTaxLines}
                    onTaxLinesClear={() => setEditState(prev => ({ ...prev, tax_line_ids: [] }))}
                    togglesDisabled={defaultsTogglesDisabled}
                  />
                </div>
              </Card>
            </div>
            <div style={{ margin: "24px" }}>
              <Card
                title="Sales channels"
                subtitle="Control where these products are available for sale (e.g., Online Store, POS)."
              >
                <div style={{ marginTop: "24px" }}>
                  <SalesChannelTable
                    state={editState}
                    setState={setEditState}
                    togglesDisabled={defaultsTogglesDisabled}
                  />
                </div>
              </Card>
            </div>
          </Card>
        )}

        <Popup
          open={deleteModalVisibility}
          type="warning"
          title={t("Deletion Confirmation")}
          description={deleteDescription}
          onOk={handleConfirmDelete}
          okText={t("Delete")}
          onCancel={() => setDeleteModalVisibility(false)}
          backDropCancel
        />

        <Popup
          open={deleteChildrenWarningOpen}
          type="warning"
          title={t("Cannot delete")}
          description={deleteChildrenWarningMessage}
          okText={t("Ok")}
          onOk={handleCloseChildrenWarning}
          onCancel={handleCloseChildrenWarning}
          backDropCancel
        />

        <Portal isMounted={deleteMigrationOpen}>
          <Sheet
            open={deleteMigrationOpen}
            title={t("Products must be migrated")}
            supportText={t(
              "This item has products assigned. Select a destination department to migrate them to before deletion.",
            )}
            okText={t("Delete")}
            cancelText={t("Cancel")}
            okDisabled={deleteMigrationTargetId == null}
            onOk={handleConfirmMigrateAndDelete}
            onCancel={() => {
              setDeleteMigrationOpen(false);
              setDeleteMigrationTargetId(null);
            }}
            backDropCancel
            size="small"
            overflow
          >
            <div style={{ marginTop: "12px" }}>
              <Select
                label={t("Migration target")}
                placeholder={t("Select a destination")}
                onChange={(value: number) => setDeleteMigrationTargetId(Number(value))}
                defaultValue={deleteMigrationTargetId ?? undefined}
              >
                {(getMigrationTargets(getCurrentDepartmentFromAllDepartments()) ?? []).map(target => (
                  <Option
                    key={target.id}
                    value={target.id}
                    name={target.title}
                    className={`migration-target migration-target--${target.type} migration-target--depth-${target.depth}`}
                  >
                    <span className="migration-target__label">{target.title}</span>
                  </Option>
                ))}
              </Select>
            </div>
          </Sheet>
        </Portal>

        <Popup
          open={applyDefaultsPopupOpen}
          type="warning"
          title={t("Apply new default values?")}
          description={""}
          onOk={() => {
            void saveDepartment();
          }}
          okText={t("Save Changes")}
          cancelText={t("Cancel")}
          onCancel={() => {
            setEditState(prev => ({ ...prev, updateProductsStatus: "only_new" }));
            setApplyDefaultsPopupOpen(false);
          }}
          backDropCancel
        >
          <div className="flex flex-col gap-3">
            <Checkbox
              size="small"
              circle
              label={"Only new products"}
              supportText="Existing products will not change"
              onChange={() => setEditState(prev => ({ ...prev, updateProductsStatus: "only_new" }))}
              checked={editState.updateProductsStatus === "only_new"}
            />
            <Checkbox
              size="small"
              circle
              label={"All new and existing products"}
              supportText={"Overwrite current values for products assigned to " + editState?.title}
              onChange={() => setEditState(prev => ({ ...prev, updateProductsStatus: "all_new_and_existing" }))}
              checked={editState.updateProductsStatus === "all_new_and_existing"}
            />
          </div>
        </Popup>
      </Page>
    </div>
  );
}
